// v10/src/components/admin/AdminSquawks.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  AlertTriangle, CheckCircle2, Wrench,
  ChevronDown, ChevronUp, X, Plus, RefreshCw, Loader,
  Camera, Image as ImageIcon, Trash2,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import api from '../../services/apiService';

const formatDate     = (d) => d ? new Date(d).toLocaleDateString('es-AR', { day:'2-digit', month:'2-digit', year:'numeric' }) : '—';
const formatDateTime = (d) => {
  if (!d) return '—';
  const dt   = new Date(d);
  const date = dt.toLocaleDateString('es-AR', { day:'2-digit', month:'2-digit', year:'numeric' });
  const time = `${String(dt.getHours()).padStart(2,'0')}:${String(dt.getMinutes()).padStart(2,'0')}`;
  return `${date} ${time}hs`;
};

const fileToBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload  = () => resolve(reader.result.split(',')[1]);
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

// ── Severity & Status data — defined via hooks inside components ──
const SEVERITY_COLORS = {
  aog:      { color: '#ef4444', bg: 'rgba(239,68,68,0.1)',  border: 'rgba(239,68,68,0.25)',  icon: '🔴' },
  mel:      { color: '#d97706', bg: 'rgba(217,119,6,0.1)',  border: 'rgba(217,119,6,0.25)',  icon: '🟡' },
  cosmetic: { color: '#16a34a', bg: 'rgba(22,163,74,0.1)',  border: 'rgba(22,163,74,0.25)', icon: '🟢' },
};
const STATUS_COLORS = {
  open:        { color: '#ef4444' },
  in_progress: { color: '#d97706' },
  closed:      { color: '#16a34a' },
};

// ── Badges ────────────────────────────────────────────────────────
const SeverityBadge = ({ severity }) => {
  const { t } = useTranslation('admin');
  const s = SEVERITY_COLORS[severity] || SEVERITY_COLORS.cosmetic;
  const label = severity === 'aog' ? t('severity_aog') : severity === 'mel' ? t('severity_mel') : t('severity_cosmetic');
  return (
    <span style={{ display:'inline-flex', alignItems:'center', gap:'4px', padding:'3px 10px', borderRadius:'20px', fontSize:'11px', fontWeight:700, background:s.bg, border:`1px solid ${s.border}`, color:s.color, whiteSpace:'nowrap' }}>
      {s.icon} {label}
    </span>
  );
};

const StatusBadge = ({ status }) => {
  const { t } = useTranslation('admin');
  const s = STATUS_COLORS[status] || STATUS_COLORS.open;
  const label = status === 'open' ? t('status_open') : status === 'in_progress' ? t('status_in_progress') : t('status_closed');
  return (
    <span style={{ display:'inline-flex', alignItems:'center', gap:'4px', padding:'3px 10px', borderRadius:'20px', fontSize:'11px', fontWeight:700, background:`${s.color}15`, border:`1px solid ${s.color}30`, color:s.color, whiteSpace:'nowrap' }}>
      {status==='closed'?'✅':status==='in_progress'?'🔧':'⚠️'} {label}
    </span>
  );
};

// ── PhotoUploader ─────────────────────────────────────────────────
const PhotoUploader = ({ photos, onChange, darkMode, maxPhotos = 3 }) => {
  const inputRef = useRef(null);
  const handleFile = async (file) => {
    if (!file || !file.type.startsWith('image/')) return;
    const base64 = await fileToBase64(file);
    onChange([...photos, { base64, mediaType:file.type, preview:URL.createObjectURL(file), name:file.name }]);
  };
  const handleInput = (e) => { const f = e.target.files?.[0]; if (f) handleFile(f); e.target.value = ''; };
  const removePhoto = (idx) => onChange(photos.filter((_, i) => i !== idx));
  return (
    <div>
      <p style={{ margin:'0 0 8px', fontSize:'12px', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.06em', color:darkMode?'rgba(255,255,255,0.4)':'#94a3b8' }}>
        Fotos del defecto ({photos.length}/{maxPhotos})
      </p>
      <div style={{ display:'flex', gap:'8px', flexWrap:'wrap' }}>
        {photos.map((p, i) => (
          <div key={i} style={{ position:'relative', width:'72px', height:'72px', borderRadius:'12px', overflow:'hidden', flexShrink:0 }}>
            <img src={p.preview} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }}/>
            <button onClick={()=>removePhoto(i)} style={{ position:'absolute', top:'3px', right:'3px', width:'20px', height:'20px', borderRadius:'50%', border:'none', background:'rgba(0,0,0,0.6)', color:'white', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', padding:0 }}>
              <X style={{ width:'11px', height:'11px' }}/>
            </button>
          </div>
        ))}
        {photos.length < maxPhotos && (
          <motion.button type="button" onClick={()=>inputRef.current?.click()} whileTap={{ scale:0.95 }}
            style={{ width:'72px', height:'72px', borderRadius:'12px', border:`2px dashed ${darkMode?'rgba(255,255,255,0.15)':'#e2e8f0'}`, background:'transparent', cursor:'pointer', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:'4px', color:darkMode?'rgba(255,255,255,0.3)':'#94a3b8', flexShrink:0 }}>
            <Camera style={{ width:'20px', height:'20px' }}/>
            <span style={{ fontSize:'9px', fontWeight:700 }}>Foto</span>
          </motion.button>
        )}
      </div>
      <input ref={inputRef} type="file" accept="image/*" capture="environment" onChange={handleInput} style={{ display:'none' }}/>
    </div>
  );
};

// ── Modal crear squawk ────────────────────────────────────────────
const CreateSquawkModal = ({ aircraft: initialAircraft, allAircraft = [], onCreated, onClose, darkMode }) => {
  const { t } = useTranslation('admin');
  const [selectedAircraftId, setSelectedAircraftId] = useState(initialAircraft?.id || '');
  const currentAircraft = allAircraft.find(a => a.id === selectedAircraftId) || initialAircraft;
  const [form, setForm]       = useState({ title:'', description:'', severity:'mel' });
  const [photos, setPhotos]   = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  // ESC para cerrar (consistencia con modales nuevos de A6.1/A8/A9).
  useEffect(() => {
    const handleEsc = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  const SEVERITY_DATA = {
    aog:      { icon:'🔴', label:t('severity_aog'),      desc:t('severity_aog_desc'),      color:SEVERITY_COLORS.aog.color,      bg:SEVERITY_COLORS.aog.bg },
    mel:      { icon:'🟡', label:t('severity_mel'),      desc:t('severity_mel_desc'),      color:SEVERITY_COLORS.mel.color,      bg:SEVERITY_COLORS.mel.bg },
    cosmetic: { icon:'🟢', label:t('severity_cosmetic'), desc:t('severity_cosmetic_desc'), color:SEVERITY_COLORS.cosmetic.color, bg:SEVERITY_COLORS.cosmetic.bg },
  };

  const inputClass = { width:'100%', padding:'10px 14px', borderRadius:'12px', border:darkMode?'1px solid rgba(255,255,255,0.12)':'1px solid #e2e8f0', background:darkMode?'rgba(255,255,255,0.05)':'#f8fafc', color:darkMode?'white':'#0f172a', fontSize:'14px', fontFamily:'inherit', outline:'none', boxSizing:'border-box' };

  const handleSubmit = async () => {
    if (!form.title.trim() || !form.description.trim()) { setError(t('title_desc_required')); return; }
    setLoading(true); setError('');
    try {
      const squawkRes = await api.post('/api/squawks', { aircraft_id:currentAircraft?.id||selectedAircraftId, title:form.title.trim(), description:form.description.trim(), severity:form.severity });
      const squawkId = squawkRes.data.data?.id;
      if (squawkId && photos.length > 0) {
        await Promise.allSettled(photos.map(p => api.post('/api/images/squawk/upload', { squawkId, image:p.base64, mediaType:p.mediaType })));
      }
      if (squawkId) api.post(`/api/squawks/${squawkId}/notify`).catch(()=>{});
      onCreated(); onClose();
    } catch (err) { setError(err.response?.data?.message || 'Error al crear el squawk'); }
    finally { setLoading(false); }
  };

  return (
    <motion.div initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
      style={{ position:'fixed', inset:0, zIndex:100, display:'flex', alignItems:'center', justifyContent:'center', padding:'16px', background:'rgba(0,0,0,0.6)', backdropFilter:'blur(6px)' }}
      onClick={onClose}>
      <motion.div initial={{ scale:0.92, y:20 }} animate={{ scale:1, y:0 }} exit={{ scale:0.92, y:20 }}
        style={{ width:'100%', maxWidth:'500px', borderRadius:'24px', background:darkMode?'rgba(15,23,42,0.98)':'white', border:darkMode?'1px solid rgba(255,255,255,0.1)':'1px solid #e2e8f0', padding:'28px', boxShadow:'0 24px 60px rgba(0,0,0,0.35)', maxHeight:'90vh', overflowY:'auto' }}
        onClick={e => e.stopPropagation()}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'20px' }}>
          <div>
            <h3 style={{ margin:0, fontSize:'18px', fontWeight:800, color:darkMode?'white':'#0f172a' }}>{t('report_squawk')}</h3>
            <p style={{ margin:'3px 0 0', fontSize:'13px', color:darkMode?'rgba(255,255,255,0.4)':'#64748b' }}>
              {currentAircraft?.manufacturer} {currentAircraft?.model_name} {currentAircraft?.registration?`(${currentAircraft.registration})`:''}
            </p>
          </div>
          <button onClick={onClose} style={{ padding:'8px', borderRadius:'10px', border:'none', background:'transparent', cursor:'pointer', color:darkMode?'rgba(255,255,255,0.4)':'#94a3b8' }}><X style={{ width:'16px', height:'16px' }}/></button>
        </div>

        {allAircraft.length > 1 && (
          <div style={{ marginBottom:'16px' }}>
            <p style={{ margin:'0 0 8px', fontSize:'12px', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.06em', color:darkMode?'rgba(255,255,255,0.4)':'#94a3b8' }}>Aeronave</p>
            <select value={selectedAircraftId} onChange={e=>setSelectedAircraftId(e.target.value)} style={{ ...inputClass, cursor:'pointer', appearance:'auto' }}>
              {allAircraft.map(a => <option key={a.id} value={a.id}>{a.manufacturer} {a.model_name}{a.registration?` (${a.registration})`:''}</option>)}
            </select>
          </div>
        )}

        <div style={{ marginBottom:'16px' }}>
          <p style={{ margin:'0 0 8px', fontSize:'12px', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.06em', color:darkMode?'rgba(255,255,255,0.4)':'#94a3b8' }}>Severidad</p>
          <div style={{ display:'flex', gap:'8px' }}>
            {Object.entries(SEVERITY_DATA).map(([key, s]) => (
              <motion.button key={key} type="button" onClick={()=>setForm(f=>({...f,severity:key}))} whileTap={{ scale:0.96 }}
                style={{ flex:1, padding:'10px 8px', borderRadius:'12px', border:`2px solid ${form.severity===key?s.color:darkMode?'rgba(255,255,255,0.1)':'#e2e8f0'}`, background:form.severity===key?s.bg:'transparent', cursor:'pointer', fontFamily:'inherit', transition:'all 0.15s' }}>
                <div style={{ fontSize:'16px', marginBottom:'2px' }}>{s.icon}</div>
                <div style={{ fontSize:'11px', fontWeight:800, color:form.severity===key?s.color:darkMode?'rgba(255,255,255,0.4)':'#94a3b8' }}>{s.label}</div>
                <div style={{ fontSize:'9px', color:darkMode?'rgba(255,255,255,0.25)':'#94a3b8', lineHeight:1.3, marginTop:'2px' }}>{s.desc}</div>
              </motion.button>
            ))}
          </div>
        </div>

        <div style={{ marginBottom:'12px' }}>
          <p style={{ margin:'0 0 6px', fontSize:'12px', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.06em', color:darkMode?'rgba(255,255,255,0.4)':'#94a3b8' }}>Título</p>
          <input value={form.title} onChange={e=>setForm(f=>({...f,title:e.target.value}))} placeholder="Ej: Faro de aterrizaje no enciende" style={inputClass} maxLength={200}/>
        </div>

        <div style={{ marginBottom:'16px' }}>
          <p style={{ margin:'0 0 6px', fontSize:'12px', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.06em', color:darkMode?'rgba(255,255,255,0.4)':'#94a3b8' }}>Descripción</p>
          <textarea value={form.description} onChange={e=>setForm(f=>({...f,description:e.target.value}))} placeholder="Describí el defecto con el mayor detalle posible..." rows={3} style={{ ...inputClass, resize:'vertical', minHeight:'80px' }}/>
        </div>

        <div style={{ marginBottom:'16px' }}>
          <PhotoUploader photos={photos} onChange={setPhotos} darkMode={darkMode} maxPhotos={3}/>
        </div>

        {form.severity === 'aog' && (
          <motion.div initial={{ opacity:0, y:-8 }} animate={{ opacity:1, y:0 }}
            style={{ padding:'10px 14px', borderRadius:'12px', background:'rgba(239,68,68,0.08)', border:'1px solid rgba(239,68,68,0.2)', marginBottom:'16px', display:'flex', gap:'8px', alignItems:'flex-start' }}>
            <AlertTriangle style={{ width:'15px', height:'15px', color:'#ef4444', flexShrink:0, marginTop:'1px' }}/>
            <p style={{ margin:0, fontSize:'12px', color:'#ef4444', fontWeight:600, lineHeight:1.5 }}>
              Al reportar AOG, el avión quedará marcado como fuera de servicio y los pilotos verán la advertencia antes de seleccionarlo.
            </p>
          </motion.div>
        )}

        {error && <div style={{ padding:'10px 14px', borderRadius:'12px', background:'rgba(239,68,68,0.08)', border:'1px solid rgba(239,68,68,0.2)', marginBottom:'14px' }}><p style={{ margin:0, fontSize:'13px', color:'#ef4444' }}>{error}</p></div>}

        <div style={{ display:'flex', gap:'10px' }}>
          <motion.button type="button" onClick={handleSubmit} disabled={loading}
            whileHover={!loading?{ scale:1.02 }:{}} whileTap={!loading?{ scale:0.97 }:{}}
            style={{ flex:1, padding:'13px', borderRadius:'14px', border:'none', cursor:loading?'not-allowed':'pointer', background:SEVERITY_COLORS[form.severity]?.color||'#3b82f6', color:'white', fontWeight:700, fontSize:'14px', fontFamily:'inherit', display:'flex', alignItems:'center', justifyContent:'center', gap:'8px' }}>
            {loading?<Loader style={{ width:'16px', height:'16px', animation:'spin 1s linear infinite' }}/>:<Plus style={{ width:'16px', height:'16px' }}/>}
            {loading?(photos.length>0?'Subiendo fotos...':'Guardando...'):t('report_squawk')}
          </motion.button>
          <button type="button" onClick={onClose}
            style={{ padding:'13px 18px', borderRadius:'14px', border:darkMode?'1px solid rgba(255,255,255,0.1)':'1px solid #e2e8f0', background:'transparent', cursor:'pointer', fontWeight:600, fontSize:'14px', color:darkMode?'rgba(255,255,255,0.5)':'#64748b', fontFamily:'inherit' }}>
            {t('cancel', { ns:'common' })}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

// ── Modal cerrar squawk ───────────────────────────────────────────
const CloseSquawkModal = ({ squawk, onClosed, onClose, darkMode }) => {
  const { t } = useTranslation('admin');
  const [notes, setNotes]     = useState('');
  const [photos, setPhotos]   = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  // ESC para cerrar (consistencia con modales nuevos de A6.1/A8/A9).
  useEffect(() => {
    const handleEsc = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  const handleClose = async () => {
    if (!notes.trim()) { setError('Describí cómo se resolvió el problema'); return; }
    setLoading(true); setError('');
    try {
      await api.put(`/api/squawks/${squawk.id}`, { status:'closed', resolution_notes:notes.trim() });
      window.dispatchEvent(new CustomEvent('condor360:squawk-updated'));
      if (photos.length > 0) {
        await Promise.allSettled(photos.map(p => api.post('/api/images/squawk/upload', { squawkId:squawk.id, image:p.base64, mediaType:p.mediaType })));
      }
      onClosed(); onClose();
    } catch (err) { setError(err.response?.data?.message || 'Error al cerrar el squawk'); }
    finally { setLoading(false); }
  };

  return (
    <motion.div initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
      style={{ position:'fixed', inset:0, zIndex:100, display:'flex', alignItems:'center', justifyContent:'center', padding:'16px', background:'rgba(0,0,0,0.6)', backdropFilter:'blur(6px)' }}
      onClick={onClose}>
      <motion.div initial={{ scale:0.92, y:20 }} animate={{ scale:1, y:0 }} exit={{ scale:0.92, y:20 }}
        style={{ width:'100%', maxWidth:'440px', borderRadius:'24px', background:darkMode?'rgba(15,23,42,0.98)':'white', border:darkMode?'1px solid rgba(255,255,255,0.1)':'1px solid #e2e8f0', padding:'28px', boxShadow:'0 24px 60px rgba(0,0,0,0.35)' }}
        onClick={e => e.stopPropagation()}>
        <div style={{ textAlign:'center', marginBottom:'20px' }}>
          <div style={{ width:'56px', height:'56px', borderRadius:'50%', background:'rgba(22,163,74,0.1)', border:'2px solid rgba(22,163,74,0.2)', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 12px' }}>
            <CheckCircle2 style={{ width:'26px', height:'26px', color:'#16a34a' }}/>
          </div>
          <h3 style={{ margin:'0 0 4px', fontSize:'18px', fontWeight:800, color:darkMode?'white':'#0f172a' }}>{t('close_squawk_title')}</h3>
          <p style={{ margin:0, fontSize:'13px', color:darkMode?'rgba(255,255,255,0.4)':'#64748b' }}>"{squawk.title}"</p>
        </div>
        <div style={{ marginBottom:'14px' }}>
          <p style={{ margin:'0 0 6px', fontSize:'12px', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.06em', color:darkMode?'rgba(255,255,255,0.4)':'#94a3b8' }}>¿Cómo se resolvió?</p>
          <textarea value={notes} onChange={e=>setNotes(e.target.value)} placeholder="Describí la reparación o acción tomada..." rows={3}
            style={{ width:'100%', padding:'10px 14px', borderRadius:'12px', border:darkMode?'1px solid rgba(255,255,255,0.12)':'1px solid #e2e8f0', background:darkMode?'rgba(255,255,255,0.05)':'#f8fafc', color:darkMode?'white':'#0f172a', fontSize:'14px', fontFamily:'inherit', outline:'none', boxSizing:'border-box', resize:'vertical', minHeight:'80px' }}/>
        </div>
        <div style={{ marginBottom:'16px' }}>
          <PhotoUploader photos={photos} onChange={setPhotos} darkMode={darkMode} maxPhotos={2}/>
          <p style={{ margin:'6px 0 0', fontSize:'11px', color:darkMode?'rgba(255,255,255,0.25)':'#94a3b8' }}>Opcional — foto del componente reparado como evidencia</p>
        </div>
        {error && <div style={{ padding:'10px 14px', borderRadius:'12px', background:'rgba(239,68,68,0.08)', border:'1px solid rgba(239,68,68,0.2)', marginBottom:'14px' }}><p style={{ margin:0, fontSize:'13px', color:'#ef4444' }}>{error}</p></div>}
        <div style={{ display:'flex', gap:'10px' }}>
          <motion.button type="button" onClick={handleClose} disabled={loading}
            whileHover={!loading?{ scale:1.02 }:{}} whileTap={!loading?{ scale:0.97 }:{}}
            style={{ flex:1, padding:'13px', borderRadius:'14px', border:'none', cursor:loading?'not-allowed':'pointer', background:'linear-gradient(135deg,#16a34a,#15803d)', color:'white', fontWeight:700, fontSize:'14px', fontFamily:'inherit', display:'flex', alignItems:'center', justifyContent:'center', gap:'8px' }}>
            {loading?<Loader style={{ width:'16px', height:'16px', animation:'spin 1s linear infinite' }}/>:<CheckCircle2 style={{ width:'16px', height:'16px' }}/>}
            {loading?'Cerrando...':t('confirm_close')}
          </motion.button>
          <button type="button" onClick={onClose}
            style={{ padding:'13px 18px', borderRadius:'14px', border:darkMode?'1px solid rgba(255,255,255,0.1)':'1px solid #e2e8f0', background:'transparent', cursor:'pointer', fontWeight:600, fontSize:'14px', color:darkMode?'rgba(255,255,255,0.5)':'#64748b', fontFamily:'inherit' }}>
            {t('cancel', { ns:'common' })}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

// ── Galería de fotos inline ───────────────────────────────────────
const PhotoGallery = ({ photos }) => {
  if (!photos?.length) return null;
  return (
    <div style={{ display:'flex', gap:'8px', flexWrap:'wrap', marginTop:'10px' }}>
      {photos.map((p, i) => (
        <a key={i} href={p.url} target="_blank" rel="noopener noreferrer"
          style={{ display:'block', width:'64px', height:'64px', borderRadius:'10px', overflow:'hidden', flexShrink:0 }}>
          <img src={p.url} alt={`foto-${i+1}`} style={{ width:'100%', height:'100%', objectFit:'cover' }}/>
        </a>
      ))}
    </div>
  );
};

// ── Fila de squawk ────────────────────────────────────────────────
const SquawkRow = ({ squawk, onUpdate, onClose: onCloseSquawk, userRole, darkMode }) => {
  const { t } = useTranslation('admin');
  const [expanded, setExpanded]             = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  const handleInProgress = async () => {
    setUpdatingStatus(true);
    try {
      await api.put(`/api/squawks/${squawk.id}`, { status:'in_progress' });
      window.dispatchEvent(new CustomEvent('condor360:squawk-updated'));
      onUpdate();
    }
    finally { setUpdatingStatus(false); }
  };

  const isStaff = ['admin','instructor'].includes(userRole);
  const sev     = SEVERITY_COLORS[squawk.severity] || SEVERITY_COLORS.cosmetic;
  const photos  = squawk.photos || [];

  return (
    <motion.div initial={{ opacity:0, y:8 }} animate={{ opacity:1, y:0 }}
      style={{ borderRadius:'16px', border:squawk.severity==='aog'&&squawk.status!=='closed'?'1.5px solid rgba(239,68,68,0.35)':(darkMode?'1px solid rgba(255,255,255,0.07)':'1px solid #f1f5f9'), background:darkMode?'rgba(255,255,255,0.03)':'white', marginBottom:'8px', overflow:'hidden' }}>
      <div style={{ padding:'14px 16px', display:'flex', alignItems:'center', gap:'12px', cursor:'pointer' }} onClick={()=>setExpanded(e=>!e)}>
        <div style={{ width:'36px', height:'36px', borderRadius:'12px', background:sev.bg, border:`1px solid ${sev.border}`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:'16px', flexShrink:0 }}>{sev.icon}</div>
        <div style={{ flex:1, minWidth:0 }}>
          <p style={{ margin:'0 0 5px', fontWeight:700, fontSize:'14px', color:darkMode?'white':'#0f172a', lineHeight:1.3 }}>{squawk.title}</p>
          <div style={{ display:'flex', alignItems:'center', gap:'6px', flexWrap:'wrap' }}>
            <SeverityBadge severity={squawk.severity}/>
            <StatusBadge status={squawk.status}/>
            {photos.length > 0 && (
              <span style={{ display:'inline-flex', alignItems:'center', gap:'3px', fontSize:'10px', fontWeight:600, color:darkMode?'rgba(255,255,255,0.35)':'#94a3b8' }}>
                <ImageIcon style={{ width:'11px', height:'11px' }}/> {photos.length}
              </span>
            )}
          </div>
          <p style={{ margin:'5px 0 0', fontSize:'12px', color:darkMode?'rgba(255,255,255,0.35)':'#94a3b8' }}>
            {squawk.aircraft_name}{squawk.registration?` · ${squawk.registration}`:''} · {squawk.reporter_name} · {formatDate(squawk.created_at)}
          </p>
        </div>
        {expanded?<ChevronUp style={{ width:'16px', height:'16px', color:darkMode?'rgba(255,255,255,0.3)':'#94a3b8', flexShrink:0 }}/>:<ChevronDown style={{ width:'16px', height:'16px', color:darkMode?'rgba(255,255,255,0.3)':'#94a3b8', flexShrink:0 }}/>}
      </div>
      <AnimatePresence>
        {expanded && (
          <motion.div initial={{ height:0, opacity:0 }} animate={{ height:'auto', opacity:1 }} exit={{ height:0, opacity:0 }} transition={{ duration:0.2 }} style={{ overflow:'hidden' }}>
            <div style={{ padding:'0 16px 16px', borderTop:darkMode?'1px solid rgba(255,255,255,0.06)':'1px solid #f1f5f9' }}>
              <p style={{ margin:'14px 0 4px', fontSize:'12px', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.06em', color:darkMode?'rgba(255,255,255,0.35)':'#94a3b8' }}>Descripción</p>
              <p style={{ margin:0, fontSize:'14px', color:darkMode?'rgba(255,255,255,0.7)':'#374151', lineHeight:1.6 }}>{squawk.description}</p>
              {photos.length > 0 && (
                <>
                  <p style={{ margin:'12px 0 4px', fontSize:'12px', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.06em', color:darkMode?'rgba(255,255,255,0.35)':'#94a3b8' }}>Fotos ({photos.length})</p>
                  <PhotoGallery photos={photos}/>
                </>
              )}
              {squawk.resolution_notes && (
                <>
                  <p style={{ margin:'14px 0 4px', fontSize:'12px', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.06em', color:'#16a34a' }}>Resolución</p>
                  <p style={{ margin:0, fontSize:'14px', color:darkMode?'rgba(74,222,128,0.8)':'#15803d', lineHeight:1.6 }}>{squawk.resolution_notes}</p>
                  <p style={{ margin:'4px 0 0', fontSize:'11px', color:darkMode?'rgba(255,255,255,0.25)':'#94a3b8' }}>Cerrado por {squawk.closer_name} el {formatDateTime(squawk.closed_at)}</p>
                </>
              )}
              {isStaff && squawk.status !== 'closed' && (
                <div style={{ display:'flex', gap:'8px', marginTop:'14px', flexWrap:'wrap' }}>
                  {squawk.status === 'open' && (
                    <motion.button type="button" onClick={handleInProgress} disabled={updatingStatus} whileTap={{ scale:0.97 }}
                      style={{ display:'flex', alignItems:'center', gap:'6px', padding:'8px 14px', borderRadius:'10px', border:'none', cursor:'pointer', background:'rgba(217,119,6,0.1)', color:'#d97706', fontWeight:700, fontSize:'12px', fontFamily:'inherit' }}>
                      <Wrench style={{ width:'13px', height:'13px' }}/> {t('mark_in_progress')}
                    </motion.button>
                  )}
                  <motion.button type="button" onClick={()=>onCloseSquawk(squawk)} whileTap={{ scale:0.97 }}
                    style={{ display:'flex', alignItems:'center', gap:'6px', padding:'8px 14px', borderRadius:'10px', border:'none', cursor:'pointer', background:'rgba(22,163,74,0.1)', color:'#16a34a', fontWeight:700, fontSize:'12px', fontFamily:'inherit' }}>
                    <CheckCircle2 style={{ width:'13px', height:'13px' }}/> {t('close_squawk')}
                  </motion.button>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// ── Componente principal ──────────────────────────────────────────
const AdminSquawks = ({ darkMode, userRole }) => {
  const { t } = useTranslation('admin');
  const [squawks, setSquawks]               = useState([]);
  const [aircraft, setAircraft]             = useState([]);
  const [loading, setLoading]               = useState(true);
  const [error, setError]                   = useState('');
  const [filterStatus,   setFilterStatus]   = useState('');
  const [filterSeverity, setFilterSeverity] = useState('');
  const [filterAircraft, setFilterAircraft] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [closingSquawk, setClosingSquawk]   = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const params = new URLSearchParams();
      if (filterStatus)   params.append('status',      filterStatus);
      if (filterSeverity) params.append('severity',    filterSeverity);
      if (filterAircraft) params.append('aircraft_id', filterAircraft);
      const [squawksRes, aircraftRes] = await Promise.all([
        api.get(`/api/squawks?${params.toString()}&limit=50`),
        api.get('/api/aircraft?is_active=true'),
      ]);
      setSquawks(squawksRes.data.data || []);
      setAircraft(aircraftRes.data.data || []);
    } catch { setError('Error al cargar los squawks'); }
    finally { setLoading(false); }
  }, [filterStatus, filterSeverity, filterAircraft]);

  useEffect(() => { load(); }, [load]);

  const openSquawks   = squawks.filter(s => s.status !== 'closed');
  const aogSquawks    = openSquawks.filter(s => s.severity === 'aog');
  const closedSquawks = squawks.filter(s => s.status === 'closed');

  const card = {
    background: darkMode?'rgba(255,255,255,0.03)':'white',
    border: darkMode?'1px solid rgba(255,255,255,0.08)':'1px solid #e2e8f0',
    borderRadius:'20px', padding:'24px', boxShadow:'0 2px 12px rgba(0,0,0,0.06)',
  };

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:'24px' }}>
      <style>{`@keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }`}</style>

      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:'12px' }}>
        <div>
          <h1 style={{ margin:'0 0 4px', fontSize:'24px', fontWeight:900, color:darkMode?'white':'#0f172a', letterSpacing:'-0.5px' }}>{t('squawks_title')}</h1>
          <p style={{ margin:0, fontSize:'14px', color:darkMode?'rgba(255,255,255,0.4)':'#64748b' }}>{t('squawks_desc')}</p>
        </div>
        <div style={{ display:'flex', gap:'10px', alignItems:'center' }}>
          <motion.button type="button" onClick={load} whileTap={{ scale:0.95 }}
            style={{ padding:'8px', borderRadius:'12px', border:darkMode?'1px solid rgba(255,255,255,0.1)':'1px solid #e2e8f0', background:'transparent', cursor:'pointer', color:darkMode?'rgba(255,255,255,0.5)':'#64748b', display:'flex' }}>
            <RefreshCw style={{ width:'16px', height:'16px' }}/>
          </motion.button>
          <motion.button type="button" onClick={()=>setShowCreateModal(true)} whileHover={{ scale:1.02 }} whileTap={{ scale:0.97 }}
            style={{ display:'flex', alignItems:'center', gap:'7px', padding:'10px 18px', borderRadius:'12px', border:'none', cursor:'pointer', background:'linear-gradient(135deg,#ef4444,#dc2626)', color:'white', fontWeight:700, fontSize:'13px', fontFamily:'inherit' }}>
            <Plus style={{ width:'15px', height:'15px' }}/> {t('report_squawk')}
          </motion.button>
        </div>
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(150px, 1fr))', gap:'12px' }}>
        {[
          { label:t('squawk_stat_aog'),        value:aogSquawks.length,                                       color:'#ef4444', icon:'🔴' },
          { label:t('squawk_stat_mel'),         value:openSquawks.filter(s=>s.severity!=='aog').length,        color:'#d97706', icon:'🟡' },
          { label:t('squawk_stat_inprogress'),  value:squawks.filter(s=>s.status==='in_progress').length,      color:'#3b82f6', icon:'🔧' },
          { label:t('squawk_stat_closed'),      value:closedSquawks.length,                                    color:'#16a34a', icon:'✅' },
        ].map((s, i) => (
          <div key={i} style={{ ...card, display:'flex', alignItems:'center', gap:'12px', padding:'16px' }}>
            <div style={{ width:'40px', height:'40px', borderRadius:'12px', background:`${s.color}15`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:'18px', flexShrink:0 }}>{s.icon}</div>
            <div>
              <p style={{ margin:0, fontSize:'22px', fontWeight:900, color:darkMode?'white':'#0f172a', lineHeight:1 }}>{s.value}</p>
              <p style={{ margin:'2px 0 0', fontSize:'11px', fontWeight:600, color:darkMode?'rgba(255,255,255,0.35)':'#94a3b8' }}>{s.label}</p>
            </div>
          </div>
        ))}
      </div>

      {aogSquawks.length > 0 && (
        <motion.div initial={{ opacity:0, y:-8 }} animate={{ opacity:1, y:0 }}
          style={{ padding:'14px 18px', borderRadius:'16px', background:'rgba(239,68,68,0.08)', border:'1.5px solid rgba(239,68,68,0.3)', display:'flex', alignItems:'center', gap:'12px' }}>
          <motion.div animate={{ scale:[1,1.1,1] }} transition={{ duration:1.5, repeat:Infinity }}>
            <AlertTriangle style={{ width:'20px', height:'20px', color:'#ef4444', flexShrink:0 }}/>
          </motion.div>
          <p style={{ margin:0, fontSize:'14px', fontWeight:700, color:'#ef4444' }}>
            {(() => {
              const uniqueAircraft = [...new Set(aogSquawks.map(s=>s.aircraft_name).filter(Boolean))];
              return `${uniqueAircraft.length===1?t('aog_active_one'):t('aog_active_other',{count:uniqueAircraft.length})} — ${uniqueAircraft.join(', ')}`;
            })()}
          </p>
        </motion.div>
      )}

      <div style={{ ...card, padding:'16px', display:'flex', gap:'10px', flexWrap:'wrap' }}>
        <select value={filterStatus} onChange={e=>setFilterStatus(e.target.value)}
          style={{ padding:'8px 12px', borderRadius:'10px', border:darkMode?'1px solid rgba(255,255,255,0.1)':'1px solid #e2e8f0', background:darkMode?'rgba(255,255,255,0.05)':'white', color:darkMode?'white':'#0f172a', fontSize:'13px', fontFamily:'inherit', cursor:'pointer', outline:'none' }}>
          <option value="">{t('all_statuses')}</option>
          <option value="open">{t('squawk_open_statuses')}</option>
          <option value="in_progress">{t('squawk_in_progress_status')}</option>
          <option value="closed">{t('squawk_closed_status')}</option>
        </select>
        <select value={filterSeverity} onChange={e=>setFilterSeverity(e.target.value)}
          style={{ padding:'8px 12px', borderRadius:'10px', border:darkMode?'1px solid rgba(255,255,255,0.1)':'1px solid #e2e8f0', background:darkMode?'rgba(255,255,255,0.05)':'white', color:darkMode?'white':'#0f172a', fontSize:'13px', fontFamily:'inherit', cursor:'pointer', outline:'none' }}>
          <option value="">{t('all_severities')}</option>
          <option value="aog">{t('squawk_label_aog')}</option>
          <option value="mel">{t('squawk_label_mel')}</option>
          <option value="cosmetic">{t('squawk_label_cosmetic')}</option>
        </select>
        <select value={filterAircraft} onChange={e=>setFilterAircraft(e.target.value)}
          style={{ padding:'8px 12px', borderRadius:'10px', border:darkMode?'1px solid rgba(255,255,255,0.1)':'1px solid #e2e8f0', background:darkMode?'rgba(255,255,255,0.05)':'white', color:darkMode?'white':'#0f172a', fontSize:'13px', fontFamily:'inherit', cursor:'pointer', outline:'none' }}>
          <option value="">{t('all_aircraft')}</option>
          {aircraft.map(a => <option key={a.id} value={a.id}>{a.manufacturer} {a.model_name}{a.registration?` (${a.registration})`:''}</option>)}
        </select>
      </div>

      <div style={card}>
        {loading ? (
          <div style={{ display:'flex', justifyContent:'center', padding:'40px 0' }}>
            <Loader style={{ width:'28px', height:'28px', color:'#2563eb', animation:'spin 1s linear infinite' }}/>
          </div>
        ) : error ? (
          <p style={{ margin:0, color:'#ef4444', textAlign:'center', padding:'20px' }}>{error}</p>
        ) : squawks.length === 0 ? (
          <div style={{ textAlign:'center', padding:'48px 0' }}>
            <div style={{ fontSize:'48px', marginBottom:'12px' }}>✅</div>
            <p style={{ margin:0, fontSize:'16px', fontWeight:700, color:darkMode?'white':'#0f172a' }}>{t('no_squawks')}</p>
            <p style={{ margin:'6px 0 0', fontSize:'13px', color:darkMode?'rgba(255,255,255,0.35)':'#94a3b8' }}>{t('no_squawks_desc')}</p>
          </div>
        ) : (
          squawks.map(sq => (
            <SquawkRow key={sq.id} squawk={sq} onUpdate={load} onClose={setClosingSquawk} userRole={userRole} darkMode={darkMode}/>
          ))
        )}
      </div>

      <AnimatePresence>
        {showCreateModal && (
          <CreateSquawkModal aircraft={aircraft[0]} allAircraft={aircraft} onCreated={load} onClose={()=>setShowCreateModal(false)} darkMode={darkMode}/>
        )}
        {closingSquawk && (
          <CloseSquawkModal squawk={closingSquawk} onClosed={load} onClose={()=>setClosingSquawk(null)} darkMode={darkMode}/>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminSquawks;
